import React, { useState } from "react";
import { Info, FileSpreadsheet, FileText, Users, ChevronDown, Eye, Send } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { GroupedCalDueTable } from "../components/GroupedCalDueTable";
import { EquipmentModal } from "../components/EquipmentModal";
import { CalDueByCustomerModal } from "../components/CalDueByCustomerModal";
import { CalDueRecipientsModal } from "../components/CalDueRecipientsModal";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const TABS = [
  ["due_soon_31", "Next 31 days", "due_soon", 31],
  ["due_soon_61", "Next 61 days", "due_soon", 61],
  ["overdue", "Overdue", "overdue", 0],
  ["historical", "By Month", "historical", 0],
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
// Follow-up to SPT-PTE-2026-0038: any month, either direction — at least 10
// years back, plus a comfortable few years forward (calibration due dates
// already project a few years out).
const CURRENT_YEAR = new Date().getFullYear();
const HISTORICAL_YEARS = Array.from({ length: 16 }, (_, i) => CURRENT_YEAR + 5 - i);
// Same 7-discipline canonical list used elsewhere (e.g. Job Flow Board's
// workload chart) — SPT-PTE-2026-0064.
const DISCIPLINES = ["Dimensional", "Pressure", "Torque", "Electronics", "Temperature", "Hydraulics", "Mass"];

export default function Due() {
  const { can, user } = useAuth();
  const canEdit = can("equipment", "edit");
  const [tabKey, setTabKey] = useState("due_soon_61");
  const [sel, setSel] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [byCustomer, setByCustomer] = useState(false);
  const [recipModal, setRecipModal] = useState(null); // null | "preview" | "send"
  // "By Month" picker — any month/year, either direction — plus a Due/
  // Completed mode toggle. Defaults to the current month/year, "Due" mode.
  const [histYear, setHistYear] = useState(CURRENT_YEAR);
  const [histMonth, setHistMonth] = useState(new Date().getMonth() + 1);
  const [histMode, setHistMode] = useState("due"); // "due" | "completed"
  const [discipline, setDiscipline] = useState(""); // "" = all disciplines — SPT-PTE-2026-0064

  const tab = TABS.find(([k]) => k === tabKey) || TABS[1];
  const [, , filter, days] = tab;
  const isHistorical = filter === "historical";
  const isCompleted = isHistorical && histMode === "completed";

  const columns = [
    { key: "customer_name", label: "Customer", sortable: true, colSearch: true, render: (v) => <span className="t-strong">{v || "—"}</span> },
    { key: "serial_number", label: "Serial #", sortable: true, colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "issue_number", label: "Issue #", sortable: true, colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "part_number", label: "Part #", sortable: true, colSearch: true, mono: true, render: (v) => v || <span className="t-mut">—</span> },
    { key: "description", label: "Description", sortable: true, colSearch: true, render: (v) => v || "—" },
    { key: "manufacturer", label: "Manufacturer", sortable: true, colSearch: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "owner_email", label: "Owner of Tool", sortable: true, colSearch: true, render: (v) => v
        ? <a href={`mailto:${v}`} className="mono" style={{ color: "var(--gold-deep)" }} data-testid="due-owner-email">{v}</a>
        : <span className="t-mut">—</span> },
    isCompleted
      ? { key: "calibration_date", label: "Completed Date", sortable: true, mono: true, render: (v) => <span style={{ fontWeight: 700 }}>{fmtDate(v)}</span> }
      : { key: "calibration_due_date", label: "Cal Due Date", sortable: true, mono: true, render: (v, row) => {
          const d = row.days_remaining;
          return <span style={{ fontWeight: 700, color: d != null && d < 0 ? "var(--red)" : d != null && d <= 31 ? "var(--amber)" : "var(--text-1)" }}>
            {fmtDate(v)}{d != null && <span className="t-mut" style={{ marginLeft: 6, fontWeight: 500, fontSize: 11 }}>({d < 0 ? `${Math.abs(d)}d overdue` : `${d}d`})</span>}
          </span>;
        } },
  ];

  const exportFile = async (format) => {
    if (isHistorical) return; // export scoped to the 3 live windows only, see notice
    setExporting(true);
    try {
      const params = { filter, days, format };
      const res = await api.get("/calibration-due/export", {
        params,
        responseType: "blob",
        timeout: 300000,
      });
      const mime = format === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const window_label = filter === "overdue" ? "overdue" : `due-${days}d`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `calibration-due-${window_label}-${today}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} ready — check your downloads`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setExporting(false); }
  };

  // SPT-PTE-2026-0044 — clicking a grouped customer opens a new Receiving/
  // New Job tab pre-filled with that customer + every one of their due
  // tools as equipment lines, ready to action without re-typing anything.
  // Reuses the existing Receiving-draft mechanism (server-persisted, so it
  // survives independently of the new tab) — Receiving.js resumes it via
  // ?resumeDraft=<id> on mount.
  const openJobForCustomer = async (g) => {
    const items = g.rows.map((r) => ({
      description: r.description || "",
      manufacturer: r.manufacturer || "",
      part_number: r.part_number || "",
      serial_number: r.serial_number || "",
      asset_number: r.asset_number || "",
      issue_number: r.issue_number || "",
      location: r.location || "Toolstore",
      cal_method: (r.sanas_or_traceable || "").toLowerCase().startsWith("sanas") ? "sanas" : "traceable",
      group_item: false, group_number: "",
      discipline: r.discipline || "Dimensional",
      technician_id: "",
      calibration_period_days: r.calibration_period_days || 365,
      quantity: 1,
      receiving_condition: "In box/case/pouch",
      remarks: "", notes: "",
      priority: "normal",
      send_to_subcontractor: false, subcontractor_id: "", subcontractor_name: "",
      subcontractor_reason: "", delivery_method: "",
      last_subcontractor_name: r.sub_contractor_name || null,
      last_subcontractor_id: r.sub_contractor_id || null,
      matched_equipment_id: r.id,
      matched_meta: { last: r.last_calibration_date, due: r.calibration_due_date, history: null },
      cross_customer_warning: null,
    }));
    try {
      const payload = { customer: { id: g.customerId, customer_name: g.customerName, customer_number: g.customerNumber }, items };
      const res = await api.post("/receiving/drafts", { payload });
      const draftId = res.data?.id;
      if (!draftId) throw new Error("Draft was not created");
      window.open(`/app/receiving?resumeDraft=${draftId}`, "_blank", "noopener");
      toast.success(`Opened a New Job tab pre-filled for ${g.customerName} (${items.length} tool${items.length !== 1 ? "s" : ""})`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const isGrouped = tabKey === "due_soon_31" || tabKey === "due_soon_61";
  const canEmail = (user?.role === "manager" || user?.role === "admin") && filter === "due_soon" && (days === 31 || days === 61);

  return (
    <>
      <PageHead crumb="Operations" title="Calibration Due"
        sub="Cal due = calibration date + period. Click any row for the full equipment record & history. Scrapped & out-of-service items are excluded automatically."
        actions={
          !isHistorical && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="btn gold" data-testid="due-actions-trigger">
                Actions <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEmail && (
                <>
                  <DropdownMenuItem onSelect={() => setRecipModal("preview")} data-testid="due-actions-preview">
                    <Eye size={14} /> Preview
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setRecipModal("send")} data-testid="due-actions-send">
                    <Send size={14} /> Send Automated Emails
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onSelect={() => exportFile("xlsx")} disabled={exporting} data-testid="due-export-xlsx">
                <FileSpreadsheet size={14} /> {exporting ? "Exporting…" : "Export Excel"}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => exportFile("pdf")} disabled={exporting} data-testid="due-export-pdf">
                <FileText size={14} /> {exporting ? "Exporting…" : "Export PDF"}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setByCustomer(true)} data-testid="due-export-by-customer">
                <Users size={14} /> Export per customer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )
        } />
      <div className="notice" style={{ marginBottom: 14 }}><Info size={16} /><span>
        {isHistorical
          ? <><b>By Month browse:</b> pick any month &amp; year — past, present or future — plus <b>Due</b> (what was/will be due for calibration) or <b>Completed</b> (what was actually calibrated) to spot recurring workload patterns. Covers {HISTORICAL_YEARS[HISTORICAL_YEARS.length - 1]}–{HISTORICAL_YEARS[0]}. Filter by customer using the column-search box in the Customer column.</>
          : <><b>Business rule enforced:</b> due dates are recomputed (never trusted from legacy), and scrapped equipment never appears here. Filter by customer using the column-search box in the Customer column.</>}
      </span></div>

      {isGrouped ? (
        <GroupedCalDueTable endpoint="/calibration-due" params={{ filter, days, discipline: discipline || undefined, limit: 2000 }}
          columns={columns} refreshSignal={refresh}
          onRowClick={(row) => setSel(row.id)} onOpenCustomer={openJobForCustomer}
          testid="due-grouped"
          emptyText={filter === "overdue" ? "Nothing overdue." : "Nothing due in this window."}
          searchPlaceholder="Search customer, serial, asset, part, description…"
          toolbar={<>
            {TABS.map(([k, label]) => (
              <button key={k} className={`ftab ${tabKey === k ? "active" : ""}`}
                      data-testid={`due-tab-${k}`} onClick={() => setTabKey(k)}>
                {label}
              </button>
            ))}
            <select className="input" style={{ maxWidth: 170, marginLeft: 4 }} value={discipline}
              onChange={(e) => setDiscipline(e.target.value)} data-testid="due-discipline-filter">
              <option value="">All disciplines</option>
              {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </>} />
      ) : (
      <DataTable endpoint="/calibration-due"
        params={isHistorical ? { filter, year: histYear, month: histMonth, mode: histMode, discipline: discipline || undefined, limit: 2000 } : { filter, days, discipline: discipline || undefined, limit: 2000 }}
        columns={columns} rowKey="id" testid="due" clientSide
        defaultSort={{ by: "customer_name", dir: "asc" }}
        searchPlaceholder="Search customer, serial, asset, part, description…"
        emptyText={isHistorical ? (isCompleted ? "No calibrations were completed in this month." : "No calibrations were due in this month.") : (filter === "overdue" ? "Nothing overdue." : "Nothing due in this window.")}
        onRowClick={(row) => setSel(row.id)} refreshSignal={refresh}
        toolbar={<>
          {TABS.map(([k, label]) => (
            <button key={k} className={`ftab ${tabKey === k ? "active" : ""}`}
                    data-testid={`due-tab-${k}`} onClick={() => setTabKey(k)}>
              {label}
            </button>
          ))}
          <select className="input" style={{ maxWidth: 170, marginLeft: 4 }} value={discipline}
            onChange={(e) => setDiscipline(e.target.value)} data-testid="due-discipline-filter">
            <option value="">All disciplines</option>
            {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {isHistorical && (
            <span style={{ display: "inline-flex", gap: 6, marginLeft: 4 }}>
              <span style={{ display: "inline-flex", gap: 2, padding: 3, background: "var(--card-2)", borderRadius: 8 }}>
                <button className={`ftab ${histMode === "due" ? "active" : ""}`}
                  onClick={() => setHistMode("due")} data-testid="due-historical-mode-due">Due</button>
                <button className={`ftab ${histMode === "completed" ? "active" : ""}`}
                  onClick={() => setHistMode("completed")} data-testid="due-historical-mode-completed">Completed</button>
              </span>
              <select className="input" style={{ maxWidth: 140 }} value={histMonth}
                onChange={(e) => setHistMonth(Number(e.target.value))} data-testid="due-historical-month">
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select className="input" style={{ maxWidth: 100 }} value={histYear}
                onChange={(e) => setHistYear(Number(e.target.value))} data-testid="due-historical-year">
                {HISTORICAL_YEARS.slice().reverse().map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </span>
          )}
        </>} />
      )}

      {sel && <EquipmentModal id={sel} canEdit={canEdit} onClose={() => setSel(null)} onChanged={() => setRefresh((x) => x + 1)} />}
      {byCustomer && (
        <CalDueByCustomerModal filter={filter} days={days}
          windowLabel={filter === "overdue" ? "overdue" : `due-${days}d`}
          onClose={() => setByCustomer(false)} />
      )}
      {recipModal && (
        <CalDueRecipientsModal filter={filter} days={days || 61} mode={recipModal}
          onClose={() => setRecipModal(null)} />
      )}
    </>
  );
}
